import cn from 'classnames';
import cloneDeep from 'lodash/cloneDeep';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useImmer } from 'use-immer';

import { ReportStatusOptions } from '@webapp/account/src/components/survey/results/lib';
import { useSavedTask } from '@webapp/account/src/hooks/use-saved-task';
import { ResultsContext } from '@webapp/account/src/pages/survey/results/context';
import { selectMultiLang } from '@webapp/account/src/resources/multi-lang/selectors';
import { selectWithFilters } from '@webapp/account/src/resources/reports/selectors';
import { AccountRoutes } from '@webapp/account/src/resources/routes';
import { selectMinFilterDate, selectSaveNotComplete } from '@webapp/account/src/resources/survey/selectors';
import { FileType, TaskLocalStatus, TaskType } from '@webapp/account/src/resources/tasks/task';

import { DateRangeFilter } from '@webapp/common/components/date-range-filter';
import { Alert } from '@webapp/common/components/message';
import { Select } from '@webapp/common/components/select';
import { isSubdomain } from '@webapp/common/lib/api';
import { crc32 } from '@webapp/common/lib/crc32';

import { PublicResultsRoutes } from '@webapp/public-results/src/results-routes';

import { FiltersSwitch } from '../filters-switch';
import { Footer } from '../footer';

import { fileTypes, formatsPerType, reportOptions, ReportType } from './lib';

import css from './results.css';

export const Results: FC<{
    surveyId: number;
    hideSingle: boolean;
    hasTestQuestions?: boolean;
    hasTestsOnFewPages?: boolean;
    multiLang?: string;
    defaultLang?: string;
}> = ({ hideSingle, surveyId, hasTestQuestions, hasTestsOnFewPages, multiLang, defaultLang }) => {
    const { globalFilters } = useContext(ResultsContext);
    const savingNotComplete = useSelector(selectSaveNotComplete);
    const [dateRange, setDateRange] = useImmer({
        dateFrom: globalFilters.dateFrom,
        dateTo: globalFilters.dateTo,
    });
    const [reportStatus, setReportStatus] = useState(globalFilters.reportStatus);
    const [lang, setLang] = useState<string>(defaultLang);
    const withFilters = useSelector(selectWithFilters);
    const [useFilters, setUseFilters] = useState<boolean>(withFilters);
    const [onlyTestScore, setOnlyTestScore] = useState<boolean>(false);
    const [pageScores, setPageScores] = useState<boolean>(false);
    const filtersHash = crc32(JSON.stringify(globalFilters));
    const { complete, createTask, forget, link, progress, size, status, taskStatus } = useSavedTask(
        filtersHash,
        `s_${surveyId}`,
    );

    const [reportType, setReportType] = useState<ReportType>(ReportType.TABLES_AND_DIAGRAMS);
    const [selectedType, setSelectedType] = useState<FileType>(formatsPerType[reportType][0]);
    const taskLoading = status === TaskLocalStatus.LOADING;
    const taskError = status === TaskLocalStatus.ERROR;
    const downloadDisabled = !selectedType || taskLoading;

    let taskType = reportType === ReportType.SINGLE_ANSWERS ? TaskType.RESP : TaskType.RSLT;

    if (reportType === ReportType.CONSOLIDATED_REPORT && selectedType === FileType.SPSS) {
        taskType = TaskType.ASPS;
    }

    const pdfReport = taskType === TaskType.RSLT && selectedType === FileType.PDF;
    const minDate = useSelector(selectMinFilterDate);
    const { surveyLanguages, allLanguages } = useSelector(selectMultiLang);

    const handleSelectType = useCallback((type: FileType) => () => setSelectedType(type), []);

    const downloadPdf = useCallback(() => {
        createTask({
            surveyId,
            fileType: FileType.PDF,
            taskType: TaskType.PDF,
            globalFilters: useFilters
                ? { ...globalFilters, ...dateRange, reportStatus }
                : { ...dateRange, reportStatus },
            lang,
            params: {
                // TODO implement common path fn or move to backend
                path: isSubdomain ? PublicResultsRoutes.common.link : AccountRoutes.resultsCommon.create(surveyId),
            },
        });
    }, [createTask, lang, dateRange, globalFilters, reportStatus, surveyId, useFilters]);

    const handleClickDownload = () => {
        let filters = cloneDeep(globalFilters);
        let fileType = null;

        if (selectedType === FileType.XLSX && reportType === ReportType.TABLES_AND_DIAGRAMS) {
            fileType = FileType.XLSX_DIA;
        } else if (selectedType !== FileType.SPSS) {
            fileType = selectedType;
        }

        if (!useFilters) {
            filters.reportStatus = reportStatus;
            delete filters.filters;
        }

        filters = Object.assign(filters, dateRange);

        createTask({
            surveyId,
            fileType,
            taskType,
            onlyTestScore,
            pageScores,
            lang,
            globalFilters: filters,
        });
    };

    const handleTypeChange = useCallback((val) => {
        setReportType(val);
        setSelectedType(formatsPerType[val][0]);
    }, []);

    const onDownload = useCallback(() => forget(), [forget]);
    const handleDateRange = useCallback(({ from, to }) => setDateRange({ dateFrom: from, dateTo: to }), [setDateRange]);
    const handleReportStatus = useCallback((s) => setReportStatus(Number(s)), []);

    const reportLanguageOptions = useMemo(() => {
        const langs = surveyLanguages
            .filter((item) => item.status === 'full')
            .map((item) => ({ label: item.nameRu, value: item.code }));

        if (defaultLang && !langs.find((item) => item.value === defaultLang)) {
            const findFullDefaultLangObject = allLanguages.find((item) => item.code === defaultLang);

            langs.push({ value: defaultLang, label: findFullDefaultLangObject?.nameRu || defaultLang });
        }

        return langs;
    }, [surveyLanguages, allLanguages]);

    useEffect(() => {
        if (taskError) {
            Alert.error('Ошибка при формировании отчета');
        }
    }, [taskError]);

    const reportTypeOptions = useMemo(() => (hideSingle ? reportOptions.slice(0, -1) : reportOptions), [hideSingle]);

    return (
        <div className={css.results}>
            <div className={cn(css.group, css.type)}>
                <div className={css.label}>Тип отчета</div>
                <div className={css.fields}>
                    <div className={css.type}>
                        <Select options={reportTypeOptions} value={reportType} onChange={handleTypeChange} />
                    </div>
                    {savingNotComplete && (
                        <div className={css.answers}>
                            <Select
                                options={ReportStatusOptions}
                                value={globalFilters.reportStatus}
                                onChange={handleReportStatus}
                                stretchMenuToLongestOption={true}
                            />
                        </div>
                    )}
                </div>
            </div>
            <div className={cn(css.group, css.format)}>
                <div className={css.label}>Формат</div>
                <div className={css.fields}>
                    <div className={css.formats}>
                        {fileTypes.map((type) => (
                            <button
                                key={type.value}
                                className={cn(css.format, { [css.active]: selectedType === type.value })}
                                disabled={!formatsPerType[reportType.toString()].includes(type.value)}
                                onClick={handleSelectType(type.value)}
                            >
                                {type.label}
                            </button>
                        ))}
                    </div>
                    <FiltersSwitch
                        defaultChecked={useFilters}
                        disabled={!withFilters}
                        label='С учетом фильтров'
                        onChange={setUseFilters}
                    />
                    {hasTestQuestions && reportType === ReportType.CONSOLIDATED_REPORT && (
                        <FiltersSwitch
                            defaultChecked={onlyTestScore}
                            label='Только баллы'
                            onChange={setOnlyTestScore}
                        />
                    )}
                    {hasTestsOnFewPages &&
                        reportType === ReportType.CONSOLIDATED_REPORT &&
                        (selectedType === FileType.CSV || selectedType === FileType.XLSX) && (
                            <FiltersSwitch
                                defaultChecked={pageScores}
                                label='Подсчёт баллов постранично'
                                onChange={setPageScores}
                            />
                        )}
                </div>
            </div>
            <div className={cn(css.group, css.format)}>
                <div className={css.label}>Период</div>
                <div className={css.fields}>
                    <DateRangeFilter
                        className={css.filters}
                        from={dateRange.dateFrom}
                        minDate={minDate}
                        to={dateRange.dateTo}
                        onChange={handleDateRange}
                    />
                </div>
            </div>
            {multiLang && reportLanguageOptions.length > 0 && (
                <div className={css.group}>
                    <div className={css.label}>Язык</div>
                    <div className={css.fields}>
                        <Select
                            defaultValue={lang}
                            className={css.langSelect}
                            placeholder='Язык'
                            options={reportLanguageOptions}
                            onChange={(value) => {
                                setLang(value === defaultLang ? undefined : (value as string));
                            }}
                        />
                    </div>
                </div>
            )}
            <Footer
                complete={complete}
                disabled={downloadDisabled}
                link={link}
                loading={taskLoading}
                progress={progress}
                size={size}
                taskStatus={taskStatus}
                onClick={pdfReport ? downloadPdf : handleClickDownload}
                onDownload={onDownload}
            />
        </div>
    );
};
